import { AddToCart } from '@theme/cart-api';
import { responsive } from '@theme/responsive';
import { CollectionPageState } from './collection-page-state.js';

/**
 * Collection view mode is stored as the desktop column count and mapped to its
 * paired mobile count (3↔1, 4↔2, 6↔3). The key is scoped to the collection
 * pathname. theme-browser-bootstrap.liquid mirrors this mapping before paint.
 *
 * Links marked with `data-collection-bar-link` hand the current vertical scroll
 * position to the destination collection in sessionStorage. The destination
 * consumes it once and briefly pins the position while its layout settles.
 */
const COLLECTION_VIEW_STORAGE_PREFIX = 'revolution:collection-view:';
const COLLECTION_NAVIGATION_SCROLL_STORAGE_KEY = 'revolution:collection-navigation-scroll';
const COLLECTION_NAVIGATION_SCROLL_MAX_AGE = 60_000;
const COLLECTION_NAVIGATION_SCROLL_PIN_DURATION = 1_000;
const SEARCH_DESKTOP_COLUMN_STORAGE_KEY = 'revolution:search-columns';
const SEARCH_MOBILE_COLUMN_STORAGE_KEY = 'revolution:search-columns-mobile';
const DESKTOP_COLUMN_CLASS_PREFIX = 'collection-grid__products--desktop-';
const MOBILE_COLUMN_CLASS_PREFIX = 'collection-grid__products--mobile-';
const VALID_DESKTOP_COLUMNS = ['3', '4', '6'];
const VALID_MOBILE_COLUMNS = ['1', '2', '3'];
const DEFAULT_DESKTOP_COLUMNS = '4';
const DESKTOP_TO_MOBILE_COLUMNS = Object.freeze({ 3: '1', 4: '2', 6: '3' });
const MOBILE_TO_DESKTOP_COLUMNS = Object.freeze({ 1: '3', 2: '4', 3: '6' });
const MOBILE_COLUMN_CARD_SIZE = Object.freeze({ 1: 'big', 2: 'medium', 3: 'small' });
const PROMO_ACTIVE_STATUSES = new Set(['ready', 'shifted']);
const PROMO_LAYOUTS = Object.freeze({
  desktop: {
    columns: 4,
    columnSpan: 2,
    rowSpan: 2,
    maxRow: 20,
    automaticSlots: [
      { column: 1, row: 1 },
      { column: 3, row: 4 },
      { column: 2, row: 7 },
    ],
  },
  mobile: {
    columns: 2,
    columnSpan: 2,
    rowSpan: 1,
    maxRow: 40,
    automaticSlots: [
      { column: 1, row: 3 },
      { column: 1, row: 6 },
      { column: 1, row: 9 },
    ],
  },
});

class MainCollectionGrid extends HTMLElement {
  #addToCart = null;
  #pageState = null;
  #collectionGrid = null;
  #onColumnSwitcherClick = null;
  #onToolbarReplaced = null;
  #onHolderReplaced = null;
  #onCountsUpdated = null;
  #onCollectionBarLinkClick = null;
  #onUserScrollDuringNavigationRestore = null;
  #navigationScrollPinRaf = null;
  #navigationScrollPinTimeout = null;
  #promoResizeCleanup = null;
  #promoMutationObserver = null;
  #activePromoLayout = null;
  #collectionDesktopColumns = DEFAULT_DESKTOP_COLUMNS;

  connectedCallback() {
    if (!this.#addToCart) {
      this.#addToCart = new AddToCart(this);
    }

    if (!this.#pageState) {
      this.#pageState = new CollectionPageState(this);
      this.#pageState.connect();
    }

    this.#collectionGrid = this.closest('.collection-grid');
    this.#onColumnSwitcherClick = (event) => {
      const button = event.target.closest('[data-column-count]');
      const switcher = button?.closest('[data-column-switcher]');
      if (!button || !switcher || !this.#collectionGrid?.contains(switcher)) {
        return;
      }

      const scope = switcher.dataset.columnSwitcherScope ?? 'desktop';
      if (!this.#isSearchPage()) {
        const desktopColumns =
          scope === 'mobile'
            ? MOBILE_TO_DESKTOP_COLUMNS[button.dataset.columnCount]
            : button.dataset.columnCount;
        this.#applyCollectionColumns(desktopColumns);
        return;
      }

      if (scope === 'mobile') {
        this.#applyMobileColumns(button.dataset.columnCount);
      } else {
        this.#applyDesktopColumns(button.dataset.columnCount);
      }
    };

    this.#onToolbarReplaced = () => {
      this.#initColumnSwitcher();
    };
    this.#onHolderReplaced = () => {
      this.#initColumnSwitcher();
      this.#observePromoHolder();
      this.#updatePromoLayout();
    };

    this.#collectionGrid?.addEventListener('click', this.#onColumnSwitcherClick);
    this.#onCollectionBarLinkClick = (event) => {
      const link = event.target.closest('[data-collection-bar-link]');
      if (
        !link ||
        !this.#collectionGrid?.contains(link) ||
        !this.#isUnmodifiedLinkClick(event, link)
      ) {
        return;
      }

      const destination = this.#getCollectionNavigationLocation(link.href);
      if (!destination) {
        return;
      }

      try {
        sessionStorage.setItem(
          COLLECTION_NAVIGATION_SCROLL_STORAGE_KEY,
          JSON.stringify({
            destination,
            scrollY: window.scrollY,
            savedAt: Date.now(),
          })
        );
      } catch {
        /* Scroll persistence is optional when storage is unavailable. */
      }
    };
    this.#collectionGrid?.addEventListener('click', this.#onCollectionBarLinkClick);
    document.body.addEventListener('collection-grid:toolbar-replaced', this.#onToolbarReplaced);
    document.body.addEventListener('collection-grid:holder-replaced', this.#onHolderReplaced);
    this.#initColumnSwitcher();
    this.#restoreCollectionNavigationScroll();

    this.#onCountsUpdated = () => {
      if (!this.#isSearchPage()) {
        const mobileColumns = DESKTOP_TO_MOBILE_COLUMNS[this.#collectionDesktopColumns];
        this.#syncProductCardSizes(mobileColumns);
      }
      this.#updatePromoLayout();
    };
    document.addEventListener('collection-grid:counts-updated', this.#onCountsUpdated);

    this.#activePromoLayout = this.#getActivePromoLayout();
    this.#promoResizeCleanup = responsive.addResizeListener(this, this.#handlePromoResize);
    this.#observePromoHolder();
    this.#updatePromoLayout();
  }

  disconnectedCallback() {
    this.#collectionGrid?.removeEventListener('click', this.#onColumnSwitcherClick);
    this.#collectionGrid?.removeEventListener('click', this.#onCollectionBarLinkClick);
    document.body.removeEventListener('collection-grid:toolbar-replaced', this.#onToolbarReplaced);
    document.body.removeEventListener('collection-grid:holder-replaced', this.#onHolderReplaced);
    document.removeEventListener('collection-grid:counts-updated', this.#onCountsUpdated);
    this.#promoResizeCleanup?.removeResizeListener();
    this.#promoMutationObserver?.disconnect();
    this.#collectionGrid = null;
    this.#onColumnSwitcherClick = null;
    this.#onToolbarReplaced = null;
    this.#onHolderReplaced = null;
    this.#onCountsUpdated = null;
    this.#onCollectionBarLinkClick = null;
    this.#promoResizeCleanup = null;
    this.#promoMutationObserver = null;
    this.#activePromoLayout = null;
    this.#pageState?.disconnect();
    this.#pageState = null;
    this.#addToCart = null;
    this.#stopCollectionNavigationScrollPin();
  }

  #isSearchPage() {
    return Boolean(this.closest('search-component'));
  }

  #getCollectionViewStorageKey() {
    const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
    return `${COLLECTION_VIEW_STORAGE_PREFIX}${normalizedPath}`;
  }

  #isUnmodifiedLinkClick(event, link) {
    return !(
      event.defaultPrevented ||
      (event.button != null && event.button !== 0) ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.hasAttribute('download') ||
      (link.target && link.target !== '_self')
    );
  }

  #getCollectionNavigationLocation(rawUrl) {
    try {
      const url = new URL(rawUrl, window.location.origin);
      if (url.origin !== window.location.origin) {
        return null;
      }

      const pathname = url.pathname.replace(/\/+$/, '') || '/';
      return `${pathname}${url.search}`;
    } catch {
      return null;
    }
  }

  #restoreCollectionNavigationScroll() {
    let saved = null;

    try {
      const raw = sessionStorage.getItem(COLLECTION_NAVIGATION_SCROLL_STORAGE_KEY);
      sessionStorage.removeItem(COLLECTION_NAVIGATION_SCROLL_STORAGE_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      return;
    }

    const currentLocation = this.#getCollectionNavigationLocation(window.location.href);
    const isRecent =
      Number.isFinite(saved?.savedAt) &&
      Date.now() - saved.savedAt >= 0 &&
      Date.now() - saved.savedAt <= COLLECTION_NAVIGATION_SCROLL_MAX_AGE;

    if (
      !isRecent ||
      saved.destination !== currentLocation ||
      !Number.isFinite(saved.scrollY) ||
      saved.scrollY < 0
    ) {
      return;
    }

    const restoreScroll = () => {
      const previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, saved.scrollY);
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    };

    restoreScroll();
    this.#stopCollectionNavigationScrollPin();

    this.#onUserScrollDuringNavigationRestore = () => {
      this.#stopCollectionNavigationScrollPin();
    };
    window.addEventListener('touchstart', this.#onUserScrollDuringNavigationRestore, {
      passive: true,
    });
    window.addEventListener('pointerdown', this.#onUserScrollDuringNavigationRestore, {
      passive: true,
    });
    window.addEventListener('wheel', this.#onUserScrollDuringNavigationRestore, { passive: true });
    window.addEventListener('keydown', this.#onUserScrollDuringNavigationRestore);

    const pinScroll = () => {
      restoreScroll();
      this.#navigationScrollPinRaf = requestAnimationFrame(pinScroll);
    };
    this.#navigationScrollPinRaf = requestAnimationFrame(pinScroll);
    this.#navigationScrollPinTimeout = setTimeout(
      () => this.#stopCollectionNavigationScrollPin(),
      COLLECTION_NAVIGATION_SCROLL_PIN_DURATION
    );
  }

  #stopCollectionNavigationScrollPin() {
    if (this.#navigationScrollPinRaf != null) {
      cancelAnimationFrame(this.#navigationScrollPinRaf);
      this.#navigationScrollPinRaf = null;
    }

    if (this.#navigationScrollPinTimeout != null) {
      clearTimeout(this.#navigationScrollPinTimeout);
      this.#navigationScrollPinTimeout = null;
    }

    if (this.#onUserScrollDuringNavigationRestore) {
      window.removeEventListener('touchstart', this.#onUserScrollDuringNavigationRestore);
      window.removeEventListener('pointerdown', this.#onUserScrollDuringNavigationRestore);
      window.removeEventListener('wheel', this.#onUserScrollDuringNavigationRestore);
      window.removeEventListener('keydown', this.#onUserScrollDuringNavigationRestore);
      this.#onUserScrollDuringNavigationRestore = null;
    }
  }

  #hasActiveCollectionFilters() {
    const params = new URLSearchParams(window.location.search);
    return [...params.keys()].some((key) => key.startsWith('filter.'));
  }

  #initColumnSwitcher() {
    if (this.#isSearchPage()) {
      this.#initSearchColumnSwitcherScope(
        'desktop',
        VALID_DESKTOP_COLUMNS,
        SEARCH_DESKTOP_COLUMN_STORAGE_KEY
      );
      this.#initSearchColumnSwitcherScope(
        'mobile',
        VALID_MOBILE_COLUMNS,
        SEARCH_MOBILE_COLUMN_STORAGE_KEY
      );
      return;
    }

    const columns = this.#hasActiveCollectionFilters()
      ? DEFAULT_DESKTOP_COLUMNS
      : (this.#getStoredColumns(this.#getCollectionViewStorageKey(), VALID_DESKTOP_COLUMNS) ??
        DEFAULT_DESKTOP_COLUMNS);

    this.#applyCollectionColumns(columns, { persist: false });
  }

  #initSearchColumnSwitcherScope(scope, validColumns, storageKey) {
    const switcher = this.#collectionGrid?.querySelector(
      `[data-column-switcher][data-column-switcher-scope="${scope}"]`
    );
    if (!switcher) {
      return;
    }

    const defaultColumns = switcher.dataset.defaultColumns || '4';
    const columns = this.#getStoredColumns(storageKey, validColumns) ?? defaultColumns;

    if (scope === 'mobile') {
      this.#applyMobileColumns(columns);
    } else {
      this.#applyDesktopColumns(columns);
    }
  }

  #getStoredColumns(storageKey, validColumns) {
    try {
      const stored = localStorage.getItem(storageKey);
      return validColumns.includes(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  #applyCollectionColumns(desktopColumns, { persist = true } = {}) {
    if (!VALID_DESKTOP_COLUMNS.includes(desktopColumns)) {
      return;
    }

    const mobileColumns = DESKTOP_TO_MOBILE_COLUMNS[desktopColumns];
    const gridHolder = document.getElementById('mainCollectionHolder');

    this.#collectionDesktopColumns = desktopColumns;
    document.documentElement.dataset.productGridColumnsDesktop = desktopColumns;
    document.documentElement.dataset.productGridColumnsMobile = mobileColumns;

    if (!gridHolder) {
      return;
    }

    this.#replaceColumnClass(gridHolder, DESKTOP_COLUMN_CLASS_PREFIX, desktopColumns);
    this.#replaceColumnClass(gridHolder, MOBILE_COLUMN_CLASS_PREFIX, mobileColumns);
    this.#syncProductCardSizes(mobileColumns, gridHolder);

    if (persist && !this.#hasActiveCollectionFilters()) {
      try {
        localStorage.setItem(this.#getCollectionViewStorageKey(), desktopColumns);
      } catch {
        /* ignore */
      }
    }

    this.#syncColumnSwitcherUI(desktopColumns, 'desktop');
    this.#syncColumnSwitcherUI(mobileColumns, 'mobile');
    this.#updatePromoLayout();
  }

  #replaceColumnClass(gridHolder, prefix, columns) {
    Array.from(gridHolder.classList).forEach((className) => {
      if (className.startsWith(prefix)) {
        gridHolder.classList.remove(className);
      }
    });
    gridHolder.classList.add(`${prefix}${columns}`);
  }

  #syncProductCardSizes(
    mobileColumns,
    gridHolder = document.getElementById('mainCollectionHolder')
  ) {
    if (!gridHolder) {
      return;
    }

    const cardSize = MOBILE_COLUMN_CARD_SIZE[mobileColumns] ?? 'medium';
    gridHolder.querySelectorAll('product-card').forEach((card) => {
      if (typeof card.setCardSize === 'function') {
        card.setCardSize(cardSize);
        return;
      }

      card.classList.remove('product-card--big', 'product-card--medium', 'product-card--small');
      card.classList.add(`product-card--${cardSize}`);
    });
  }

  #applyDesktopColumns(columns) {
    if (!VALID_DESKTOP_COLUMNS.includes(columns)) {
      return;
    }

    const gridHolder = document.getElementById('mainCollectionHolder');
    if (!gridHolder) {
      return;
    }

    document.documentElement.dataset.productGridColumnsDesktop = columns;
    this.#replaceColumnClass(gridHolder, DESKTOP_COLUMN_CLASS_PREFIX, columns);

    try {
      localStorage.setItem(SEARCH_DESKTOP_COLUMN_STORAGE_KEY, columns);
    } catch {
      /* ignore */
    }

    this.#syncColumnSwitcherUI(columns, 'desktop');
    this.#updatePromoLayout();
  }

  #applyMobileColumns(columns) {
    if (!VALID_MOBILE_COLUMNS.includes(columns)) {
      return;
    }

    const gridHolder = document.getElementById('mainCollectionHolder');
    if (!gridHolder) {
      return;
    }

    document.documentElement.dataset.productGridColumnsMobile = columns;
    this.#replaceColumnClass(gridHolder, MOBILE_COLUMN_CLASS_PREFIX, columns);

    try {
      localStorage.setItem(SEARCH_MOBILE_COLUMN_STORAGE_KEY, columns);
    } catch {
      /* ignore */
    }

    this.#syncColumnSwitcherUI(columns, 'mobile');
    this.#updatePromoLayout();
  }

  #syncColumnSwitcherUI(columns, scope) {
    const switcher = this.#collectionGrid?.querySelector(
      `[data-column-switcher][data-column-switcher-scope="${scope}"]`
    );
    if (!switcher) {
      return;
    }

    switcher.querySelectorAll('[data-column-count]').forEach((button) => {
      const isActive = button.dataset.columnCount === columns;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  #getMobileColumns() {
    const gridHolder = document.getElementById('mainCollectionHolder');
    if (!gridHolder) {
      return 2;
    }
    for (const className of gridHolder.classList) {
      if (className.startsWith(MOBILE_COLUMN_CLASS_PREFIX)) {
        const count = parseInt(className.slice(MOBILE_COLUMN_CLASS_PREFIX.length), 10);
        if (Number.isFinite(count) && count > 0) {
          return count;
        }
      }
    }
    return 2;
  }

  #getActivePromoLayout() {
    return responsive.matchesBreakpoint('lap_and_up') ? 'desktop' : 'mobile';
  }

  #handlePromoResize() {
    const activeLayout = this.#getActivePromoLayout();
    if (activeLayout === this.#activePromoLayout) {
      return;
    }

    this.#activePromoLayout = activeLayout;
    this.#updatePromoLayout();
  }

  #observePromoHolder() {
    this.#promoMutationObserver?.disconnect();

    const gridHolder = document.getElementById('mainCollectionHolder');
    if (!gridHolder) {
      return;
    }

    this.#promoMutationObserver ??= new MutationObserver(() => this.#updatePromoLayout());
    this.#promoMutationObserver.observe(gridHolder, { childList: true });
  }

  #parsePositiveInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
  }

  #getRequestedPromoPosition(cell, index, layoutName) {
    const layout = PROMO_LAYOUTS[layoutName];
    const useAutomaticPlacement = cell.dataset.promoPlacementMode !== 'custom';

    if (useAutomaticPlacement) {
      return layout.automaticSlots[index] ?? null;
    }

    if (layoutName === 'desktop') {
      return {
        column: this.#parsePositiveInteger(cell.dataset.promoRequestedColD, 1),
        row: this.#parsePositiveInteger(cell.dataset.promoRequestedRowD, 1),
      };
    }

    return {
      column: 1,
      row: this.#parsePositiveInteger(cell.dataset.promoRequestedRowM, 1),
    };
  }

  #rectanglesOverlap(first, second) {
    const firstColumnEnd = first.column + first.columnSpan - 1;
    const secondColumnEnd = second.column + second.columnSpan - 1;
    const firstRowEnd = first.row + first.rowSpan - 1;
    const secondRowEnd = second.row + second.rowSpan - 1;

    return !(
      firstColumnEnd < second.column ||
      secondColumnEnd < first.column ||
      firstRowEnd < second.row ||
      secondRowEnd < first.row
    );
  }

  #resolvePromoLayout(promos, layoutName) {
    const layout = PROMO_LAYOUTS[layoutName];
    const occupied = [];
    const resolutions = new Map();
    const maxStartRow = layout.maxRow - layout.rowSpan + 1;
    const maxStartColumn = layout.columns - layout.columnSpan + 1;

    promos.forEach((cell, index) => {
      const requested = this.#getRequestedPromoPosition(cell, index, layoutName);
      if (
        !requested ||
        requested.column < 1 ||
        requested.column > maxStartColumn ||
        requested.row < 1 ||
        requested.row > maxStartRow
      ) {
        resolutions.set(cell, { valid: false });
        return;
      }

      let resolvedRow = requested.row;
      let rectangle = {
        column: requested.column,
        row: resolvedRow,
        columnSpan: layout.columnSpan,
        rowSpan: layout.rowSpan,
      };

      while (
        resolvedRow <= maxStartRow &&
        occupied.some((accepted) => this.#rectanglesOverlap(rectangle, accepted))
      ) {
        resolvedRow += 1;
        rectangle = { ...rectangle, row: resolvedRow };
      }

      if (resolvedRow > maxStartRow) {
        resolutions.set(cell, { valid: false });
        return;
      }

      occupied.push(rectangle);
      resolutions.set(cell, {
        ...rectangle,
        valid: true,
        shifted: resolvedRow !== requested.row,
      });
    });

    return resolutions;
  }

  #countPromoCellsThroughRow(resolutions, endRow) {
    const cells = new Set();

    resolutions.forEach((resolution) => {
      if (!resolution.valid) {
        return;
      }

      for (let row = resolution.row; row < resolution.row + resolution.rowSpan; row += 1) {
        if (row > endRow) {
          continue;
        }

        for (
          let column = resolution.column;
          column < resolution.column + resolution.columnSpan;
          column += 1
        ) {
          cells.add(`${row}:${column}`);
        }
      }
    });

    return cells.size;
  }

  #applyResolvedPromoData(cell, desktopResolution, mobileResolution) {
    cell.dataset.promoResolutionDesktop = desktopResolution?.valid
      ? desktopResolution.shifted
        ? 'shifted'
        : 'ready'
      : 'invalid';
    cell.dataset.promoResolutionMobile = mobileResolution?.valid
      ? mobileResolution.shifted
        ? 'shifted'
        : 'ready'
      : 'invalid';

    if (desktopResolution?.valid) {
      cell.dataset.promoResolvedColD = String(desktopResolution.column);
      cell.dataset.promoResolvedRowD = String(desktopResolution.row);
      cell.style.setProperty('--promo-resolved-col-d', desktopResolution.column);
      cell.style.setProperty('--promo-resolved-row-d', desktopResolution.row);
    } else {
      delete cell.dataset.promoResolvedColD;
      delete cell.dataset.promoResolvedRowD;
      cell.style.removeProperty('--promo-resolved-col-d');
      cell.style.removeProperty('--promo-resolved-row-d');
    }

    if (mobileResolution?.valid) {
      cell.dataset.promoResolvedRowM = String(mobileResolution.row);
      cell.style.setProperty('--promo-resolved-row-m', mobileResolution.row);
    } else {
      delete cell.dataset.promoResolvedRowM;
      cell.style.removeProperty('--promo-resolved-row-m');
    }
  }

  #hydratePromoMedia(cell) {
    if (cell.dataset.promoMediaHydrated === 'true') {
      return;
    }

    const mediaHost = cell.querySelector('[data-promo-media-host]');
    const mediaTemplate = mediaHost?.querySelector(':scope > template[data-promo-media-template]');
    if (!mediaHost || !mediaTemplate) {
      cell.dataset.promoMediaHydrated = 'true';
      return;
    }

    mediaHost.appendChild(mediaTemplate.content.cloneNode(true));
    mediaTemplate.remove();
    cell.dataset.promoMediaHydrated = 'true';
  }

  #syncPromoMedia(cell, status) {
    if (PROMO_ACTIVE_STATUSES.has(status)) {
      this.#hydratePromoMedia(cell);
      return;
    }

    cell.querySelectorAll('video-component').forEach((video) => video.pause?.());
  }

  #setPromoStatus(cell, status, requiredProducts = null) {
    cell.dataset.promoStatus = status;
    cell.classList.toggle('is-pending', status === 'pending');

    if (requiredProducts === null) {
      delete cell.dataset.promoRequiredProducts;
    } else {
      cell.dataset.promoRequiredProducts = String(requiredProducts);
    }

    this.#syncPromoMedia(cell, status);
  }

  /**
   * Resolves promo rectangles before CSS receives their coordinates. Product
   * capacity is calculated from the actual occupied cells, so earlier promos
   * reduce (rather than increase) the number of products required for a lower
   * promo. This keeps row 7 valid with the initial 24-product response.
   */
  #updatePromoLayout() {
    const gridHolder = document.getElementById('mainCollectionHolder');
    const mainGrid = document.getElementById('mainGrid');
    if (!gridHolder || !mainGrid) {
      return;
    }

    const allPromos = Array.from(
      gridHolder.querySelectorAll(':scope > .collection-grid__promo-cell')
    );
    const promos = allPromos.slice(0, 3);
    if (!promos.length) {
      return;
    }

    allPromos.slice(3).forEach((cell) => this.#setPromoStatus(cell, 'invalid'));

    const shown = parseInt(mainGrid.dataset.productsShown, 10) || 0;
    const activeLayoutName = this.#getActivePromoLayout();
    this.#activePromoLayout = activeLayoutName;
    const mobileColumns = this.#getMobileColumns();
    const promosEnabled =
      !this.#hasActiveCollectionFilters() &&
      (activeLayoutName === 'desktop'
        ? this.#collectionDesktopColumns === DEFAULT_DESKTOP_COLUMNS
        : mobileColumns === 2);
    const desktopResolutions = this.#resolvePromoLayout(promos, 'desktop');
    const mobileResolutions = this.#resolvePromoLayout(promos, 'mobile');
    const activeResolutions =
      activeLayoutName === 'desktop' ? desktopResolutions : mobileResolutions;
    const activeLayout = PROMO_LAYOUTS[activeLayoutName];

    promos.forEach((cell, index) => {
      cell.dataset.promoIndex = String(index + 1);
      const desktopResolution = desktopResolutions.get(cell);
      const mobileResolution = mobileResolutions.get(cell);
      this.#applyResolvedPromoData(cell, desktopResolution, mobileResolution);

      if (!promosEnabled) {
        this.#setPromoStatus(cell, 'hidden');
        return;
      }

      const activeResolution = activeResolutions.get(cell);
      if (!activeResolution?.valid) {
        this.#setPromoStatus(cell, 'invalid');
        return;
      }

      const endRow = activeResolution.row + activeResolution.rowSpan - 1;
      const promoCells = this.#countPromoCellsThroughRow(activeResolutions, endRow);
      const requiredProducts = Math.max(0, endRow * activeLayout.columns - promoCells);
      const status =
        shown < requiredProducts ? 'pending' : activeResolution.shifted ? 'shifted' : 'ready';

      this.#setPromoStatus(cell, status, requiredProducts);
    });
  }
}

if (!customElements.get('main-collection-grid')) {
  customElements.define('main-collection-grid', MainCollectionGrid);
}
